import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { WoltModal, APIErrorNotification } from 'common/components';

type Props = {
  isLoading: boolean;
  error?: any;
  mode: 'all-areas' | 'single-area';
  onConfirm: () => void;
  onClose: () => void;
};

const Root = styled.div``;

const StyledWoltModal = styled(WoltModal)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  max-width: 378px;
  border-radius: 8px;
`;

const StyledModalContent = styled(ModalContent)`
  ${typographyCss.Body3()};
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const ConfirmContainer = styled.div`
  margin-block-start: 16px;
  display: flex;
  flex-direction: column;
`;

const DeleteInputLabel = styled.div`
  margin-block-end: 8px;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DeleteAreasModal = ({ isLoading, error, mode, onConfirm, onClose }: Props) => {
  const [inputValue, setInputValue] = useState('');

  const intl = useIntl();

  const deleteString = intl.formatMessage({ id: 'picking-route.modal-delete-string' });

  const isConfirmDisabled = isLoading || (mode === 'all-areas' && inputValue !== deleteString);

  return (
    <Root
      onKeyPress={event => {
        if (event.key === 'Enter' && !isConfirmDisabled) {
          onConfirm();
        }
      }}
    >
      <StyledWoltModal isOpen onRequestClose={onClose}>
        <ModalHeader onCloseClick={onClose}>
          <FormattedMessage
            id={
              mode === 'all-areas' ? 'picking-route.modal-delete-all-title' : 'picking-route.modal-delete-single-title'
            }
          />
        </ModalHeader>
        <StyledModalContent>
          <FormattedMessage
            id={mode === 'all-areas' ? 'picking-route.modal-delete-all-body' : 'picking-route.modal-delete-single-body'}
          />
          {mode === 'all-areas' && (
            <ConfirmContainer>
              <DeleteInputLabel>
                <FormattedMessage
                  id="picking-route.modal-delete-all-confirm"
                  values={{ strong: chunks => <strong>{chunks}</strong>, deleteString }}
                />
              </DeleteInputLabel>
              <Input value={inputValue} onChange={e => setInputValue(e.currentTarget.value)} disabled={isLoading} />
            </ConfirmContainer>
          )}
        </StyledModalContent>
        <StyledModalFooter>
          <ActionsContainer>
            <Button variant="secondary" size="small" onClick={onClose} disabled={isLoading}>
              <FormattedMessage id="state.cancel" />
            </Button>
            <Button
              variant="primary"
              size="small"
              style={{ marginInlineStart: 18 }}
              onClick={onConfirm}
              disabled={isConfirmDisabled}
              loading={isLoading}
            >
              <FormattedMessage id="state.confirm" />
            </Button>
          </ActionsContainer>
          {error && <APIErrorNotification error={error} style={{ marginBlockStart: 18 }} />}
        </StyledModalFooter>
      </StyledWoltModal>
    </Root>
  );
};

export default DeleteAreasModal;
